import React, { useEffect, useState } from "react";
import { Box, Card, Flex, Link, Stack, Table, Text } from "@chakra-ui/react";
import { LuArrowUpRight } from "react-icons/lu";
import { Button } from "../../components/ui/button";
import { env, STRIPE_CUSTOMER_PORTAL, useGlobalState } from "../../helpers/GlobalStateProvider";
import { openPortalOnClick } from "../../helpers/stripePortal";
import { getPlanForTier, planTiers } from "../APIPlans/planData";
import { getApiUrl } from "../../config";
import { getToken } from "../../helpers/tokenManager";
import type { PaymentRecord } from "../APIPlans/types";
import ChangePlanPicker from "./ChangePlanPicker";

const SubscriptionBillingSection: React.FC = () => {
  const { info, bearerToken } = useGlobalState();
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const response = await fetch(`${getApiUrl(env)}/v3/apiKey/payment_history`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: "{}",
        });
        if (!response.ok) return;
        const data = await response.json();
        setPaymentHistory(data?.payments || []);
      } catch {
        // Silently fail — render an empty state
      }
    };
    fetchPaymentHistory();
  }, [bearerToken]);

  // Match dashboard's logic for tier resolution.
  const effectiveTier =
    info?.stripeTier && info.stripeTier > 0
      ? info.stripeTier
      : info?.stakedTier && info.stakedTier > 0
        ? info.stakedTier
        : (info?.tier ?? 0);
  const planName = getPlanForTier(effectiveTier);

  const paymentMethod = (info as any)?.paymentMethod || null;
  const billingInterval = (info as any)?.billingInterval || null;
  const paidThrough = info?.paidThrough || null;
  const trialEnd = (info as any)?.trialEnd || null;
  const nextPaymentDateField = (info as any)?.nextPaymentDate || null;
  const nextPaymentAmountField = (info as any)?.nextPaymentAmount;

  // Show "MOR Staking" when the user's tier is staking-derived (no Stripe
  // payment on file) so this cell reflects how the plan is being paid for
  // instead of rendering a blank em-dash.
  const isStakingSubscriber =
    !paymentMethod && (info?.stakedTier ?? 0) > 0;
  const paymentMethodLabel = paymentMethod
    ? "Credit card"
    : isStakingSubscriber
      ? "MOR Staking"
      : "—";

  const isInTrial = !!trialEnd && new Date(trialEnd).getTime() > Date.now();

  const nextPaymentDateRaw =
    isInTrial ? trialEnd : (nextPaymentDateField || paidThrough);
  const formattedNextPaymentDate = nextPaymentDateRaw
    ? new Date(nextPaymentDateRaw).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  const formatTrialEnd = trialEnd
    ? new Date(trialEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

  const planForTier = planTiers.find(
    (p) => p.monthlyTier === effectiveTier || p.annualTier === effectiveTier,
  );
  const fallbackAmountDollars = planForTier
    ? billingInterval === "year"
      ? planForTier.annualBilled
      : planForTier.monthlyPrice
    : undefined;
  const nextPaymentAmount =
    typeof nextPaymentAmountField === "number"
      ? nextPaymentAmountField / 100
      : fallbackAmountDollars;

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title fontSize="30px" fontWeight="bold" letterSpacing="-0.4px" lineHeight="32px">
          Subscription & Billing
        </Card.Title>
        <Card.Description fontSize="md" color="#52525b" lineHeight="24px">
          Manage your plan, payment method, and billing history.
        </Card.Description>
      </Card.Header>
      <Card.Body>
        {/* Plan summary subsection */}
        <Stack gap={4}>
          {/* Badge row */}
          <Flex alignItems="center" gap={2} flexWrap="wrap">
            <Flex bg="#f3e8ff" borderRadius="6px" px="12px" py="4px" alignItems="center">
              <Text fontSize="sm" fontWeight="bold" color="#7c3aed">
                {planName} Plan
              </Text>
            </Flex>
            {effectiveTier > 0 && (
              <Flex bg="#f4f4f5" borderRadius="6px" px="12px" py="4px" alignItems="center">
                <Text fontSize="sm" fontWeight="bold" color="#52525b">
                  {billingInterval === "year" ? "Annual" : "Monthly"}
                </Text>
              </Flex>
            )}
            {isInTrial && (
              <Flex bg="#dbeafe" borderRadius="6px" px="12px" py="4px" alignItems="center">
                <Text fontSize="sm" fontWeight="bold" color="#1d4ed8">
                  Free Trial{formatTrialEnd ? ` · ends ${formatTrialEnd}` : ""}
                </Text>
              </Flex>
            )}
          </Flex>

          {/* NEXT PAYMENT field */}
          <Stack gap="2px">
            <Text fontSize="11px" fontWeight="semibold" letterSpacing="0.05em" textTransform="uppercase" color="#a1a1aa">
              Next payment
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="#18181b">
              {nextPaymentAmount != null ? `$${nextPaymentAmount.toFixed(2)}` : "—"}
              {nextPaymentDateRaw && (
                <Text as="span" color="#52525b" fontWeight="normal">
                  {" "}{"·"} {formattedNextPaymentDate}
                </Text>
              )}
            </Text>
          </Stack>

          {/* PAYMENT METHOD field */}
          <Stack gap="2px">
            <Text fontSize="11px" fontWeight="semibold" letterSpacing="0.05em" textTransform="uppercase" color="#a1a1aa">
              Payment method
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="#18181b">
              {paymentMethodLabel}
            </Text>
          </Stack>

          {/* Action row */}
          <Flex gap={2} flexWrap="wrap" mt={2}>
            <Button
              size="sm"
              colorPalette="purple"
              variant="solid"
              onClick={() => setIsChangingPlan(!isChangingPlan)}
            >
              Change plan
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                Manage payment method <LuArrowUpRight />
              </Link>
            </Button>
          </Flex>

          {/* Inline change plan picker */}
          <Box
            overflow="hidden"
            transition="max-height 300ms ease-in-out, opacity 300ms ease-in-out"
            maxHeight={isChangingPlan ? "1200px" : "0px"}
            opacity={isChangingPlan ? 1 : 0}
          >
            {isChangingPlan && (
              <ChangePlanPicker onClose={() => setIsChangingPlan(false)} />
            )}
          </Box>
        </Stack>

        {/* Divider */}
        <Box height="1px" bg="#e4e4e7" my={6} />

        {/* Billing history subsection */}
        <Stack gap={3}>
          <Text fontSize="md" fontWeight="bold" color="#000">
            Billing history
          </Text>
          {paymentHistory.length > 0 ? (
            <>
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Date</Table.ColumnHeader>
                    <Table.ColumnHeader>Description</Table.ColumnHeader>
                    <Table.ColumnHeader>Amount</Table.ColumnHeader>
                    <Table.ColumnHeader></Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {paymentHistory.slice(0, 10).map((payment, idx) => {
                    const isPlanChange = payment.planChange === true;
                    const isTrialActivation = payment.amount === 0 && !isPlanChange;
                    const description = isPlanChange
                      ? `Plan changed to ${payment.tierName || "new plan"}`
                      : isTrialActivation
                        ? `Trial started${payment.tierName ? ` — ${payment.tierName}` : ""}`
                        : payment.tierName ||
                          "Subscription";
                    return (
                      <Table.Row key={payment.invoiceNumber || idx}>
                        <Table.Cell>
                          {new Date(payment.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm" color={isTrialActivation || isPlanChange ? "#52525b" : "#18181b"}>
                            {description}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          {isTrialActivation || (isPlanChange && payment.amount === 0)
                            ? <Text fontSize="sm" color="#a1a1aa">{"—"}</Text>
                            : `$${(payment.amount / 100).toFixed(2)}`}
                        </Table.Cell>
                        <Table.Cell>
                          {payment.invoiceUrl && (() => {
                            const isValidInvoiceUrl =
                              typeof payment.invoiceUrl === "string" &&
                              payment.invoiceUrl.startsWith("https://") &&
                              /^https:\/\/([a-z0-9-]+\.)*stripe\.com(\/|$)/i.test(payment.invoiceUrl);
                            return isValidInvoiceUrl ? (
                              <Link href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer">
                                <LuArrowUpRight />
                              </Link>
                            ) : (
                              <Text fontSize="xs" color="#a1a1aa">Invoice</Text>
                            );
                          })()}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
              <Flex justifyContent="flex-end">
                <Button size="xs" variant="ghost" asChild>
                  <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                    View all in Stripe portal <LuArrowUpRight />
                  </Link>
                </Button>
              </Flex>
            </>
          ) : (
            <Text fontSize="sm" color="#a1a1aa">No billing history yet.</Text>
          )}
        </Stack>

        {/* Divider */}
        <Box height="1px" bg="#e4e4e7" my={6} />

        {/* Cancel subscription subsection */}
        <Stack gap={3}>
          <Text fontSize="md" fontWeight="bold" color="#000">
            Cancel subscription
          </Text>
          <Text fontSize="sm" color="#52525b">
            Your plan will remain active until the end of the current billing period. After that, you'll be on the free tier.
          </Text>
          {effectiveTier > 0 && (
            <Flex>
              <Button size="sm" variant="outline" asChild>
                <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                  Cancel subscription <LuArrowUpRight />
                </Link>
              </Button>
            </Flex>
          )}
          {effectiveTier === 0 && (
            <Text fontSize="sm" color="#a1a1aa" fontStyle="italic">
              You don't have an active subscription.
            </Text>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};

export default SubscriptionBillingSection;
